import * as _ from 'lodash';
import { StepAction } from './stepAction';
import { html5dndAction } from './scripts/html5dragAction';
import { featureFlagsService } from '../../commons/featureFlagsService';
import { html5dndAction as html5dndActionV2 } from './scripts/html5dragActionV2';
import type { SeleniumTarget } from './stepAction';
import type { VisibilityInfo } from 'clickim/src/background/visibilityUtils';
import type { IMouseEvent, MouseStep } from 'clickim/src/common/models/mouseStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

export interface DoubleClickEventData {
    elementToFocusLocatedElement: false | ILocatedElement | undefined;
    locatedElement: ILocatedElement;
    events: IMouseEvent[];
    timeout: number;
}

export interface DnDRectsAndOffsets {
    fromRect: VisibilityInfo;
    fromX: number;
    fromY: number;
    toRect: VisibilityInfo;
    toX: number;
    toY: number;
}

export class MouseStepAction extends StepAction<MouseStep> {
    private getDnDRectsAndOffsets(target: SeleniumTarget, destTarget: SeleniumTarget, clickOffset?: { x: number; y: number }, toClickOffset?: { x: number; y: number }): DnDRectsAndOffsets {
        const fromOffsets = this.stepActionUtils.getClickOffset(clickOffset, target.rectWithoutFrameOffset!);
        const toOffsets = this.stepActionUtils.getClickOffset(toClickOffset, destTarget.rectWithoutFrameOffset!);

        return {
            fromRect: target.rectWithoutFrameOffset!,
            fromX: fromOffsets.x,
            fromY: fromOffsets.y,
            toRect: destTarget.rectWithoutFrameOffset!,
            toX: toOffsets.x,
            toY: toOffsets.y,
        };
    }

    private async clickJs() {
        const { step, context } = this;
        const target = this.getTarget();
        const timeout = context.data.timeToPlayStep! + 3000;
        const events = step.events;

        if (!events?.length) {
            return { success: false, reason: 'Malformed step: missing recorded events' };
        }

        const eventMessage = {
            isRoot: target.isRoot,
            locatedElement: target.locatedElement,
            events,
            quirks: step.quirks,
            modifiers: step.modifiers,
            button: step.button,
        };

        const [{ doClick: doClickScript }, { dispatchFocus }] = await Promise.all([import('./scripts/doClick'), import('./scripts/focusElement')]);

        const doClickCode = `
            var getLocatedElement = ${this.sessionPlayerInit.codeSnippets.getLocatedElementCode};
            var dispatchFocus = ${dispatchFocus.toString()};
            var doClick = ${doClickScript.toString()};
            var eventData = arguments[0];
            var done = arguments[1];
            return doClick.call(null, eventData, done);
        `;

        try {
            type CallbackReturnType = Parameters<Parameters<typeof doClickScript>[1]>[0];
            const result = await this.driver.executeCodeAsync<CallbackReturnType, [typeof eventMessage]>(doClickCode, timeout, eventMessage);
            if (result.value?.success) {
                return { success: true };
            }
            return { success: false };
        } catch (err) {
            return {
                success: false,
                reason: err.message,
                exception: err,
            };
        }
    }

    private isWithinBounds(start: number, end: number, point: number) {
        return (point > start) && (point < end);
    }

    private getEventSequenceOffset() {
        const initialPosition = this.step.events![0]?.pointerPosition;
        if (!initialPosition) {
            return { xOffset: 0, yOffset: 0 };
        }

        const target = this.getTarget();
        const targetElementRect = target.rectWithoutFrameOffset!;
        const isXWithinBounds = this.isWithinBounds(targetElementRect.left, targetElementRect.left + targetElementRect.width, initialPosition.originX);
        const isYWithinBounds = this.isWithinBounds(targetElementRect.top, targetElementRect.top + targetElementRect.height, initialPosition.originY);
        const xOffset = isXWithinBounds ? 0 : targetElementRect.left + targetElementRect.width / 2 - initialPosition.originX;
        const yOffset = isYWithinBounds ? 0 : targetElementRect.top + targetElementRect.height / 2 - initialPosition.originY;
        return { xOffset, yOffset };
    }

    private addOffsetToEvents(offsetFromElement: { xOffset: number; yOffset: number }) {
        this.step.events!.forEach(event => {
            if (event?.pointerPosition) {
                event.pointerPosition.originX += offsetFromElement.xOffset;
                event.pointerPosition.originY += offsetFromElement.yOffset;
            }
        });
    }

    private generateEventOfType(baseEvent: IMouseEvent, type: string) {
        const cloneEvent = _.cloneDeep(baseEvent);
        cloneEvent.event = type;
        return cloneEvent;
    }

    private fixAbsoluteDragEventSequence() {
        const { events = [], allEventsOnSameElement, isHTML5Drag, toElement } = this.step;
        const downEvent = events.find(event => ['mousedown', 'pointerdown'].includes(event.event));
        if (downEvent) {
            const mousedownEventIndex = events.indexOf(downEvent);
            events.splice(mousedownEventIndex, 0, this.generateEventOfType(downEvent, 'mouseover'));
        }

        const { recordPointerMoveEvents = false } = this.context.project.defaults || {};
        const mouseUpEvent = events.find(event => event.event === 'mouseup') || (recordPointerMoveEvents && events.find(event => event.event === 'pointerup'));
        const lastMouseMoveEventIndex = Number(
            _.findLastIndex(events, event => event.event === 'mousemove') ||
            (recordPointerMoveEvents && _.findLastIndex(events, event => event.event === 'pointermove')),
        );
        if (mouseUpEvent && lastMouseMoveEventIndex > 0 && !allEventsOnSameElement) {
            events.splice(lastMouseMoveEventIndex + 1, 0, this.generateEventOfType(mouseUpEvent, 'mouseover'));
        }

        if (isHTML5Drag && !toElement) {
            this.step.events = this.addDragendIfNeeded(events);
        }

        this.addOffsetToEvents(this.getEventSequenceOffset());
    }

    private async dragPathJs() {
        const { step, context } = this;
        const target = this.getTarget();
        const timeout = context.data.timeToPlayStep! + 3000;

        if (!step.events?.length) {
            return { success: false, reason: 'Malformed step: missing recorded events' };
        }

        this.fixAbsoluteDragEventSequence();

        const events = step.events;

        const eventMessage = {
            isRoot: target.isRoot,
            locatedElement: target.locatedElement,
            events,
            quirks: step.quirks,
            modifiers: step.modifiers,
            button: step.button,
            isDrag: true,
            allEventsOnSameElement: step.allEventsOnSameElement,
        };

        const [{ doDragPath: doDragPathScript }, { dispatchFocus }] = await Promise.all([import('./scripts/doDragPath'), import('./scripts/focusElement')]);

        const doDragPathCode = `
            var getLocatedElement = ${this.sessionPlayerInit.codeSnippets.getLocatedElementCode};
            var dispatchFocus = ${dispatchFocus.toString()};
            var doDragPath = ${doDragPathScript.toString()};
            return doDragPath.apply(null, arguments);
        `;

        try {
            type CallbackReturnType = Parameters<Parameters<typeof doDragPathScript>[1]>[0];
            const result = await this.driver.executeCodeAsync<CallbackReturnType, [typeof eventMessage]>(doDragPathCode, timeout, eventMessage);
            if (result.value?.success) {
                return { success: true };
            }
            return { success: false };
        } catch (err) {
            return {
                success: false,
                reason: err.message,
                exception: err,
            };
        }
    }

    private async chooseAndRunAction() {
        const target = this.getTarget();
        const { locatedElement, seleniumElement, rectWithoutFrameOffset, rect } = target;

        // used for fallback native click
        const offsets = {
            frameOffset: {
                x: rect!.left - rectWithoutFrameOffset!.left,
                y: rect!.top - rectWithoutFrameOffset!.top,
            },
            rect: rectWithoutFrameOffset!,
            // webdriver performs offset from the center of the element, so make sure to tweak offsets accordingly
            clickOffset: this.stepActionUtils.getClickOffset(this.step.element.clickOffset, rectWithoutFrameOffset!),
        };

        // will skip left click and double click on Edge.
        const skipFileInputClick =
            featureFlagsService.flags.skipFileInputClicks.isEnabled() &&
            target.tagName === 'INPUT' &&
            (target.elementSymbol!.includes('type="file"') || target.elementSymbol!.includes('type=\'file\'') || target.elementSymbol!.includes('type=file'));

        if (skipFileInputClick) {
            return {
                keep: true,
                success: 'skipped',
                reason: 'Clicking on input type=file is disabled',
            };
        }

        // Be ware - due to bad typing `isDoubleClick` is considered a function, while it is a boolean property
        if (this.step.isDoubleClick) {
            const eventData: DoubleClickEventData = {
                elementToFocusLocatedElement: target.elementToFocusLocatedElement,
                locatedElement,
                events: this.step.events!,
                timeout: this.context.data.timeToPlayStep! + 3000,
            };
            return this.driver.doubleClick(seleniumElement, eventData, offsets);
        }
        if (this.step.isDrag) {
            if (this.step.toElement) {
                const destTarget = this.context.data.toElement as SeleniumTarget;
                if (this.step.isHTML5Drag) {
                    if (featureFlagsService.flags.usePortedHtml5DragDrop.isEnabled()) {
                        const events = this.generateHTML5DragEventSequence();
                        const timeout = this.context.data.timeToPlayStep! + 3000;
                        const contextTarget = this.getTarget();
                        const eventMessage = {
                            transactionId: `${this.context.testResultId}:${this.step.id}`,
                            id: this.step.id,
                            testResultId: this.context.testResultId,
                            eventType: this.step.type,
                            events,
                            eventData: {
                                modifiers: this.step.modifiers,
                                button: this.step.button,
                            },
                            quirks: this.step.quirks,
                            isDrag: this.step.isDrag,
                            useRecordedMousedown: this.step.useRecordedMousedown,
                            allEventsOnSameElement: this.step.allEventsOnSameElement,
                            elementToFocusLocatedElement: contextTarget.elementToFocusLocatedElement,
                            trackActiveElement: this.step.trackActiveElement,
                            locatedElement: contextTarget.locatedElement,
                            isRoot: contextTarget.isRoot,
                        };
                        const html5DNDCode = `
                        var getLocatedElement = ${this.sessionPlayerInit.codeSnippets.getLocatedElementCode};
                        var dnd = ${html5dndActionV2.toString()};
                        var eventData = arguments[0];
                        var done = arguments[1];
                        return dnd.call(null, eventData, done);
                    `;
                        type CallbackReturnType = Parameters<Parameters<typeof html5dndActionV2>[1]>[0];
                        return this.driver.executeCodeAsync<CallbackReturnType, [typeof eventMessage]>(html5DNDCode, timeout, eventMessage);
                    }

                    const html5DNDCode = `
                        var getLocatedElement = ${this.sessionPlayerInit.codeSnippets.getLocatedElementCode};
                        var dnd = ${html5dndAction.toString()};
                        var eventData = arguments[0];
                        return dnd.call(null, eventData);
                    `;

                    const eventMessage = { fromLocatedElement: locatedElement, toLocatedElement: destTarget.locatedElement };
                    return this.driver.executeJS<typeof html5dndAction>(html5DNDCode, eventMessage);
                }

                const rectsAndOffsets = this.getDnDRectsAndOffsets(target, destTarget, this.step.element.clickOffset, this.step.toElement.clickOffset);
                return this.driver.dragAndDrop(rectsAndOffsets);
            }
            return this.dragPathJs();

            // This is the old way, which uses selenium action (AKA "native" drag).
            // return this.driver.drag(seleniumElement, rectWithoutFrameOffset, xOffset, yOffset, this.step.events);
        }
        const useRightClickJS = this.driver.isSafari() && this.step.button === 2;
        const forceNativeEvent = this.driver.isSafari() && target.tagName === 'SELECT';

        if (forceNativeEvent) {
            // NOTE:
            // We want to skip click on SELECT elements because of a safari driver bug that not allowing to set the selected option after this click.
            return {
                keep: true, success: 'skipped', forceTreatAsWarning: true, reason: 'Safari does not support clicking on select elements',
            };
        }
        if (!forceNativeEvent && (!this.step.nativeEvents || useRightClickJS)) {
            return this.clickJs();
        }
        if (this.step.button === 2) {
            return this.driver.rightClick(seleniumElement, offsets);
        }
        return this.driver.leftClick(seleniumElement, offsets);
    }

    override async performAction() {
        let res: any = await this.chooseAndRunAction();
        if (!res.status && res.value && res.value.keep) {
            res = res.value;
        }
        if (res.keep) {
            delete res.keep;
            return res;
        }
        return undefined;
    }

    private addDragendIfNeeded(events: IMouseEvent[]) {
        if (events.some(event => event.event === 'dragend')) {
            return events;
        }
        const dragendDefaultEvent = {
            event: 'dragend',
            eventInfo: {
                detail: 0,
            },
            pointerPosition: this.getToElementPosition(),
        } as IMouseEvent;
        return events.concat(dragendDefaultEvent);
    }

    private getToElementPosition() {
        if (!(this.context.data?.toElement?.rect)) {
            return undefined;
        }
        const { rect } = this.context.data.toElement;
        return {
            originX: rect.left + (rect.width / 2),
            originY: rect.top + (rect.height / 2),
        };
    }

    private addDragOverBeforeDragEnd(events: IMouseEvent[]) {
        const dropOrEndEventIndex = events.findIndex(e => ['dragend', 'drop'].includes(e.event));
        const oneBeforeLastEvent = events[dropOrEndEventIndex - 1];
        if (!oneBeforeLastEvent || (oneBeforeLastEvent.event !== 'dragover')) {
            const dragenterDefaultEvent = {
                event: 'dragover',
                eventInfo: {
                    detail: 0,
                },
                pointerPosition: this.getToElementPosition(),
                fireOnTarget: true,
            } as any as IMouseEvent;
            events.splice(dropOrEndEventIndex, 0, dragenterDefaultEvent);
        } else {
            Object.assign(oneBeforeLastEvent, { fireOnTarget: true });
        }
        return events;
    }

    private fixEventSequence(events: IMouseEvent[]) {
        const withDragEnd = this.addDragendIfNeeded(events);
        return this.addDragOverBeforeDragEnd(withDragEnd);
    }

    private addElementLocatedElementToDragEvents(events: IMouseEvent[], fromElementLocatedElement: ILocatedElement, toElementLocatedElement: ILocatedElement) {
        const isOnToElement = (e: any) => e.fireOnTarget || ['drop', 'dragenter'].includes(e.event);
        events.forEach(event => Object.assign(event, { locatedElement: isOnToElement(event) ? toElementLocatedElement : fromElementLocatedElement }));
        return events;
    }

    private generateHTML5DragEventSequence() {
        const fromElement = this.context.data.targetId;
        const toElement = this.context.data.toElement;
        let events = this.step.events!.filter(event => event.event !== 'mousemove' && event.event !== 'pointermove');
        events = this.fixEventSequence(events);
        events = this.addElementLocatedElementToDragEvents(events, fromElement.locatedElement, toElement.locatedElement);
        if (this.step.dispatchDragEventsOnClosestDraggable) {
            events.forEach(event => Object.assign(event, { dispatchDragEventsOnClosestDraggable: true }));
        }
        return events;
    }
}
